import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import { useState } from 'react';

import { mergeSx } from '../../../../theme';

import { ResourcesPanel } from './ResourcesPanel';
import { resourcesStyles as styles } from './Resources.styles';

interface ResourceCard {
  title: string;
  body: string;
  footerLabel: string;
  footerBody: string;
}

const cards: ResourceCard[] = [
  {
    title: '529 Account Participation',
    body: 'The share of eligible children or families who have an active 529 college savings account.',
    footerLabel: 'Why it matters',
    footerBody:
      '529 account participation serves as a proxy for which children and families already have a head start on wealth building through access to the capital markets. Children from higher-income families are significantly more likely to have a 529 account. If 529 account participation rates are low in a community with a high number of children, we know that geography would be close to target impact.',
  },
  {
    title: 'Economic Mobility',
    body: 'Odds that a child will out earn their parents as an adult',
    footerLabel: 'Why it matters',
    footerBody:
      'Research shows that where a child grows up is more predictive of their economic future than almost any other factor. This metric tells us where opportunity has historically stalled by geography — and where 530A contributions can matter most.',
  },
];

/**
 * Resources / data-context section.
 * Figma: node 59:24267.
 */
export function Resources() {
  const [panelOpen, setPanelOpen] = useState(false);

  return (
    <Box component="section" sx={styles.root} aria-labelledby="resources-heading">
      <Box sx={styles.content}>
        <Box sx={styles.intro}>
        <Typography component="h2" id="resources-heading" sx={styles.heading}>
          Understanding the gap so
          <br />
          we can{' '}
          <Box component="span" sx={styles.headingAccent}>
            close it together
          </Box>
        </Typography>
        <Typography component="p" sx={styles.body}>
          Every family&rsquo;s path to financial security looks different. Learn about
          our methodology.
        </Typography>
        <Link
          component="button"
          type="button"
          onClick={() => setPanelOpen(true)}
          sx={styles.link}
        >
          Discover our full resource list →
        </Link>
      </Box>
      <Box sx={styles.cardsRow}>
        {cards.map((card, index) => (
          <Box key={card.title} sx={styles.card}>
            <Box
              sx={mergeSx(styles.cardTop, {
                // Single column on mobile — each card keeps its own two
                // sequential rows. Two columns from md up — every card's
                // top row is row 1, so both tops (and both bottoms, below)
                // share a row and size to whichever is taller.
                gridRow: { xs: index * 2 + 1, md: 1 },
                gridColumn: { xs: 1, md: index + 1 },
              })}
            >
              <Typography component="h3" sx={styles.cardTitle}>
                {card.title}
              </Typography>
              <Typography component="p" sx={styles.cardBody}>
                {card.body}
              </Typography>
            </Box>
            <Box
              sx={mergeSx(styles.cardBottom, {
                gridRow: { xs: index * 2 + 2, md: 2 },
                gridColumn: { xs: 1, md: index + 1 },
              })}
            >
              <Typography component="p" sx={styles.cardEyebrow}>
                {card.footerLabel}
              </Typography>
              <Typography component="p" sx={styles.cardFooter}>
                {card.footerBody}
              </Typography>
            </Box>
          </Box>
        ))}
        </Box>
      </Box>

      <ResourcesPanel open={panelOpen} onClose={() => setPanelOpen(false)} />
    </Box>
  );
}
